import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Alert, Badge, Button, Field, Page, PageHeader, Select, Table, backLinkClasses, type Column } from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import {
  BILLING_AWARD_LINK_STATUS_LABELS,
  csvCell,
  csvContent,
  csvDecimal,
  format as formatMoney,
  formatDate,
  fromColumn,
  loadFailureMessage,
} from '@evogard/domain';
import type { BillingAwardBoletoRow, BillingAwardRecordRow } from '@evogard/contract';
import { api } from '../../../lib/api.js';
import { saveCsv } from '../../../lib/csv.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { AWARDS_KEY, currentPeriod, periodLabel, recentPeriods, useAwardTenants } from './useAwards.js';

const PAGE = 200;
type Tab = 'atendimentos' | 'boletos';

/**
 * Os dados da premiação — o que entra no casamento, para conferir: os
 * atendimentos do SGA da competência e os boletos dos tipos da premiação. É a
 * tela de onde se responde "por que esse boleto não contou?".
 */
export function AwardsDataScreen() {
  const tenants = useAwardTenants();
  const [slug, setSlug] = useState('');
  const tenant = tenants.find((t) => t.slug === slug) ?? tenants[0];
  const [period, setPeriod] = useState(currentPeriod());
  const [tab, setTab] = useState<Tab>('atendimentos');
  const [search, setSearch] = useState('');
  const [pages, setPages] = useState(1);
  const q = useDebouncedTerm(search.trim());

  const records = useQuery({
    enabled: tenant !== undefined && tab === 'atendimentos',
    queryKey: [...AWARDS_KEY, 'atendimentos', tenant?.slug, period, q, pages],
    queryFn: () =>
      fetchPages<BillingAwardRecordRow>(pages, async (cursor) => {
        const r = await api.billingAwards.records({
          params: { tenant: tenant!.slug },
          query: { period, q: q.length >= 2 ? q : undefined, cursor, limit: PAGE },
        });
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os atendimentos'));
        return r.body;
      }),
  });
  const boletos = useQuery({
    enabled: tenant !== undefined && tab === 'boletos',
    queryKey: [...AWARDS_KEY, 'boletos', tenant?.slug, period, q, pages],
    queryFn: () =>
      fetchPages<BillingAwardBoletoRow>(pages, async (cursor) => {
        const r = await api.billingAwards.boletos({
          params: { tenant: tenant!.slug },
          query: { period, q: q.length >= 2 ? q : undefined, cursor, limit: PAGE },
        });
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os boletos'));
        return r.body;
      }),
  });
  const active = tab === 'atendimentos' ? records : boletos;
  const reset = () => setPages(1);

  return (
    <Page measure="cheia">
      <PageHeader
        title="Dados da premiação"
        back={
          <Link to="/billing/awards" className={backLinkClasses()}>
            Premiação da cobrança
          </Link>
        }
      />
      <div className="flex flex-wrap items-end gap-3">
        {tenants.length > 1 && (
          <div className="w-44">
            <Select
              label="Base"
              value={tenant?.slug ?? ''}
              onChange={(e) => {
                setSlug(e.target.value);
                reset();
              }}
            >
              {tenants.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="w-52">
          <Select
            label="Competência"
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              reset();
            }}
          >
            {recentPeriods().map((p) => (
              <option key={p} value={p}>
                {periodLabel(p)}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-80">
          <Field
            label="Buscar"
            placeholder={tab === 'atendimentos' ? 'Cliente, usuário do SGA, placa, protocolo ou nº' : 'Cliente, nosso número ou placa'}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              reset();
            }}
          />
        </div>
        <Button
          variant="contorno"
          disabled={!active.data || active.data.items.length === 0}
          onClick={() => {
            if (tab === 'atendimentos' && records.data) downloadRecords(records.data.items, tenant!.slug, period);
            if (tab === 'boletos' && boletos.data) downloadBoletos(boletos.data.items, tenant!.slug, period);
          }}
        >
          Exportar CSV
        </Button>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v as Tab);
          reset();
        }}
      >
        <TabsList>
          <TabsTrigger value="atendimentos">Atendimentos do SGA</TabsTrigger>
          <TabsTrigger value="boletos">Boletos dos tipos da premiação</TabsTrigger>
        </TabsList>
        <TabsContent value="atendimentos" className="mt-4 flex flex-col gap-3">
          {records.error && <Alert tone="erro">{records.error.message}</Alert>}
          <Table<BillingAwardRecordRow>
            label="Atendimentos da competência"
            empty="Nenhum atendimento do SGA nesta competência ainda. A esteira do ERP traz os do dia; o mês inteiro se pede no painel, em Buscar os atendimentos no SGA."
            items={records.data?.items ?? []}
            rowKey={(r) => String(r.id)}
            loading={records.isLoading}
            paginate={false}
            columns={RECORD_COLUMNS}
          />
        </TabsContent>
        <TabsContent value="boletos" className="mt-4 flex flex-col gap-3">
          {boletos.error && <Alert tone="erro">{boletos.error.message}</Alert>}
          <Table<BillingAwardBoletoRow>
            label="Boletos da competência"
            empty="Nenhum boleto dos tipos da premiação vence nesta competência."
            items={boletos.data?.items ?? []}
            rowKey={(b) => String(b.id)}
            loading={boletos.isLoading}
            paginate={false}
            columns={BOLETO_COLUMNS}
          />
        </TabsContent>
      </Tabs>
      {active.data?.next && (
        <div>
          <Button variant="contorno" loading={active.isFetching} onClick={() => setPages((p) => p + 1)}>
            Carregar mais
          </Button>
        </div>
      )}
    </Page>
  );
}

async function fetchPages<T>(
  pages: number,
  load: (cursor: string | undefined) => Promise<{ items: T[]; next: string | null }>,
): Promise<{ items: T[]; next: string | null }> {
  const items: T[] = [];
  let cursor: string | undefined;
  let next: string | null = null;
  for (let i = 0; i < pages; i++) {
    const page = await load(cursor);
    items.push(...page.items);
    next = page.next;
    if (next === null) break;
    cursor = next;
  }
  return { items, next };
}

const linked = (status: BillingAwardRecordRow['linkStatus']) =>
  status ? <Badge tone="sucesso">{BILLING_AWARD_LINK_STATUS_LABELS[status]}</Badge> : <span className="text-texto-suave">sem vínculo</span>;

const RECORD_COLUMNS: Array<Column<BillingAwardRecordRow>> = [
  {
    title: 'Data',
    cell: (r) => (
      <span className="flex flex-col whitespace-nowrap">
        <span>{r.servedOn ? formatDate(r.servedOn) : '—'}</span>
        <span className="text-xs text-texto-suave">nº {r.externalId}</span>
      </span>
    ),
  },
  { title: 'Cliente', alignment: 'inicio', cell: (r) => r.memberName ?? <span className="text-texto-suave">—</span> },
  { title: 'Placa', cell: (r) => r.plate ?? '—' },
  {
    title: 'Usuário do SGA',
    alignment: 'inicio',
    cell: (r) => (
      <span className="flex flex-col">
        <span>{r.erpUserName ?? r.erpUserCode ?? '—'}</span>
        <span className="text-xs text-texto-suave">
          {r.operatorName ? `operador: ${r.operatorName}` : 'não é operador da premiação'}
        </span>
      </span>
    ),
  },
  { title: 'Departamento', cell: (r) => r.departmentCode ?? '—' },
  { title: 'Tipo', alignment: 'inicio', cell: (r) => r.typeLabel ?? '—' },
  { title: 'Protocolo', cell: (r) => r.protocol ?? '—' },
  { title: 'Vínculo', cell: (r) => linked(r.linkStatus) },
];

const BOLETO_COLUMNS: Array<Column<BillingAwardBoletoRow>> = [
  {
    title: 'Vencimento',
    cell: (b) => (
      <span className="flex flex-col whitespace-nowrap">
        <span>{formatDate(b.dueDate)}</span>
        <span className="text-xs text-texto-suave">{b.nossoNumero ? `nosso nº ${b.nossoNumero}` : b.code}</span>
      </span>
    ),
  },
  { title: 'Cliente', alignment: 'inicio', cell: (b) => b.memberName ?? <span className="text-texto-suave">—</span> },
  { title: 'Placa', cell: (b) => b.plate ?? '—' },
  {
    title: 'Tipo',
    alignment: 'inicio',
    cell: (b) => (
      <span className="flex flex-col">
        <span>{b.typeLabel}</span>
        <span className="text-xs text-texto-suave">{b.ownerName ? `de ${b.ownerName}` : 'sem dono'}</span>
      </span>
    ),
  },
  { title: 'Valor', alignment: 'fim', cell: (b) => formatMoney(fromColumn(b.amount)!) },
  {
    title: 'Pagamento',
    cell: (b) =>
      b.paid ? (
        <span className="flex flex-col">
          <span>{b.paidOn ? formatDate(b.paidOn) : 'pago'}</span>
          <span className="text-xs text-texto-suave">{formatMoney(fromColumn(b.recovered)!)}</span>
        </span>
      ) : (
        <span className="text-texto-suave">em aberto</span>
      ),
  },
  { title: 'Vínculo', cell: (b) => linked(b.linkStatus) },
];

function downloadRecords(items: readonly BillingAwardRecordRow[], tenant: string, period: string): void {
  const header = ['Atendimento', 'Data', 'Cadastrado em', 'Cliente', 'Placa', 'Usuário do SGA', 'Código do usuário', 'Operador', 'Departamento', 'Tipo', 'Protocolo', 'Vínculo'];
  const rows = items.map((r) =>
    [
      r.externalId ?? '',
      r.servedOn ? formatDate(r.servedOn) : '',
      r.registeredOn ? formatDate(r.registeredOn) : '',
      r.memberName ?? '',
      r.plate ?? '',
      r.erpUserName ?? '',
      r.erpUserCode ?? '',
      r.operatorName ?? '',
      r.departmentCode ?? '',
      r.typeLabel ?? '',
      r.protocol ?? '',
      r.linkStatus ? BILLING_AWARD_LINK_STATUS_LABELS[r.linkStatus] : '',
    ].map(csvCell),
  );
  saveCsv(csvContent([header.map(csvCell), ...rows]), `premiacao-atendimentos-${period}`, tenant);
}

function downloadBoletos(items: readonly BillingAwardBoletoRow[], tenant: string, period: string): void {
  const header = ['Boleto', 'Nosso número', 'Vencimento', 'Cliente', 'Placa', 'Tipo', 'Código do tipo', 'Dono', 'Valor', 'Pago', 'Pago em', 'Recuperado', 'Vínculo'];
  const rows = items.map((b) => [
    csvCell(b.code),
    csvCell(b.nossoNumero ?? ''),
    csvCell(formatDate(b.dueDate)),
    csvCell(b.memberName ?? ''),
    csvCell(b.plate ?? ''),
    csvCell(b.typeLabel),
    csvCell(b.typeCode ?? ''),
    csvCell(b.ownerName ?? ''),
    csvDecimal(b.amount),
    csvCell(b.paid ? 'sim' : 'não'),
    csvCell(b.paidOn ? formatDate(b.paidOn) : ''),
    csvDecimal(b.recovered),
    csvCell(b.linkStatus ? BILLING_AWARD_LINK_STATUS_LABELS[b.linkStatus] : ''),
  ]);
  saveCsv(csvContent([header.map(csvCell), ...rows]), `premiacao-boletos-${period}`, tenant);
}
